import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getCookie, setCookie, migrateLocalStorage } from './cookies';

export const translations = {
  en: {
    app_title: 'Barcode Studio',
    app_subtitle: 'Create QR & 1D barcodes. Export PNG/SVG. Labels + History.',
    language: 'Language',
    tab_generate: 'Generate',
    tab_batch: 'Batch',
    tab_labels: 'Labels',
    tab_history: 'History',

    cat_2d: '2D Codes',
    cat_1d: '1D Barcodes',
    gen_subtitle: 'Create a QR code or barcode and download it.',
    popular_generators: 'Popular generators',
    popular_generators_sub: 'Jump straight to the most-used barcode and QR code generators.',
    rate_nudge_title: 'Enjoying Barcode Studio?',
    rate_nudge_desc: 'Leave a quick rating — it takes seconds and helps others.',
    rate_nudge_cta: 'Rate us',
    share_nudge_title: 'Recommend Barcode Studio',
    share_nudge_desc: 'Like the tool? Share it with colleagues and friends in one tap.',
    share_nudge_cta: 'Share',
    batch_subtitle: 'Generate many codes at once from a list or CSV.',
    labels_subtitle: 'Arrange labels on a print-ready A4 sheet.',
    history_subtitle: 'Your recently created codes.',
    step: 'Step',
    advanced: 'Advanced',
    advanced_hint: 'Extra options — only if you need them.',

    lbl_type: 'Barcode type',
    lbl_value: 'Value',
    value_placeholder: 'Enter the value to encode',
    example: 'Example',
    use_example: 'Use example',

    customization: 'Customization',
    colors: 'Colors',
    foreground: 'Foreground',
    background: 'Background',
    size: 'Size',
    bar_width: 'Bar width',
    bar_height: 'Bar height',
    qr_scale: 'Module scale',
    qr_border: 'Quiet zone',
    text: 'Text',
    show_text: 'Show human-readable text',
    text_size: 'Text size',
    bold_text: 'Bold text',
    scale_text: 'Scale text with barcode',
    font_family: 'Caption font',
    font_sans: 'Sans-serif',
    font_serif: 'Serif',
    font_mono: 'Monospace',
    font_narrow: 'Condensed',
    comparison: 'Before / After',
    comparison_before: 'Before (standard)',
    comparison_after: 'After',
    comparison_hint: 'See how bold text and scaling change the caption.',
    ad_label: 'Advertisement',

    qr_content: 'QR content',
    content_text: 'Text / URL',
    content_wifi: 'Wi-Fi access',
    content_bank: 'Bank transfer (SEPA)',
    wifi_ssid: 'Network name (SSID)',
    wifi_password: 'Password',
    wifi_encryption: 'Encryption',
    wifi_hidden: 'Hidden network',
    enc_wpa: 'WPA/WPA2/WPA3',
    enc_wep: 'WEP',
    enc_none: 'None (open)',
    wifi_hint: 'Scanning the QR code connects the device to this Wi-Fi network.',
    bank_name: 'Recipient name',
    bank_iban: 'IBAN',
    bank_bic: 'BIC (optional)',
    bank_amount: 'Amount € (optional)',
    bank_purpose: 'Reference / purpose (optional)',
    bank_hint: 'Creates a GiroCode (EPC) for SEPA transfers — scannable in most banking apps.',
    content_fields_required: 'Fill in the required fields to see a preview.',
    iban_invalid: 'IBAN checksum looks incorrect — please double-check.',
    print_wifi_card: 'Print Wi-Fi card',
    wifi_card_title: 'Guest Wi-Fi',
    wifi_card_instruction: 'Scan the QR code with your phone camera to connect.',
    wifi_card_network: 'Network',
    toggle_theme: 'Toggle dark mode',
    output: 'Output',
    output_format: 'Output format',
    reset: 'Reset',

    presets: 'Presets',
    presets_hint: 'Save your current setup and reuse it with one click.',
    preset_save: 'Save preset',
    preset_save_title: 'Save as preset',
    preset_name: 'Preset name',
    preset_name_placeholder: 'e.g. Product label QR',
    preset_load: 'Load preset',
    preset_select: 'Choose a preset…',
    preset_none: 'No presets yet',
    preset_saved: 'Preset saved.',
    preset_loaded: 'Preset loaded.',
    preset_deleted: 'Preset deleted.',
    preset_share: 'Copy share link',
    preset_link_copied: 'Share link copied to clipboard.',
    preset_from_link: 'Preset loaded from shared link.',
    preset_qr: 'Show QR code',
    preset_qr_title: 'Share preset via QR code',
    preset_qr_desc: 'Colleagues can scan this code to open the exact same configuration.',
    copy_link: 'Copy link',
    preset_delete_confirm: 'Delete this preset?',
    preset_delete_desc: 'This saved configuration will be permanently removed.',
    preset_name_required: 'Please enter a name.',
    preset_error: 'Something went wrong. Please try again.',

    preview: 'Preview',
    preview_empty: 'Choose a barcode type and enter a value to preview.',
    generating: 'Generating…',
    download: 'Download',
    download_png: 'Download PNG',
    download_svg: 'Download SVG',
    copy_payload: 'Copy value',
    print: 'Print',
    pdf: 'PDF',
    saving_pdf: 'PDF…',
    print_all: 'Print all',
    save_pdf_all: 'Save all as PDF',
    pdf_label_size: 'Label size (PDF)',
    size_small: 'Small',
    size_medium: 'Medium',
    size_large: 'Large',
    size_custom: 'Custom',
    columns: 'Columns',
    rows_label: 'Rows',
    per_page: 'per A4 page',
    a4_preview: 'A4 preview',
    pages_word: 'pages',
    copied: 'Copied to clipboard',
    saved_to_history: 'Saved to history',

    batch_intro: 'Generate many codes at once from a list or CSV file.',
    input_source: 'Input source',
    paste_list: 'Paste list',
    upload_csv: 'Upload CSV',
    paste_placeholder: 'One value per line, e.g.\nABC-001\nABC-002\nABC-003',
    csv_hint: 'CSV: first column is used as the value. Header row is auto-skipped.',
    choose_file: 'Choose CSV file',
    batch_type: 'Barcode type for all rows',
    batch_generate: 'Generate batch',
    batch_empty: 'Paste values or upload a CSV to generate many codes at once.',
    results: 'Results',
    total: 'Total',
    succeeded: 'Succeeded',
    failed: 'Failed',
    download_zip: 'Download ZIP',
    row: 'Row',

    history_empty: 'Your generated codes will appear here.',
    search: 'Search history…',
    clear_all: 'Clear all',
    delete: 'Delete',
    reuse: 'Reuse',
    no_results: 'No matching items.',
    confirm_clear_title: 'Clear all history?',
    confirm_clear_desc: 'This permanently removes all saved barcodes from this browser.',
    confirm_delete_title: 'Delete this item?',
    confirm_delete_desc: 'This permanently removes the barcode from your history.',
    cancel: 'Cancel',
    created: 'Created',

    err_empty: 'Please enter a value.',
    err_generate: 'Could not generate barcode.',
    err_batch_empty: 'Please provide at least one value.',
    deleted: 'Deleted',
    cleared: 'History cleared',

    labels_intro: 'Arrange many barcode labels on a print-ready A4 sheet — ideal for warehouse and industry.',
    label_items: 'Labels',
    label_items_hint: 'One per line: value , caption , quantity. Caption and quantity are optional.',
    label_placeholder: 'SKU-1001, Widget A, 6\nSKU-1002, Widget B, 12\nSKU-1003',
    label_layout: 'Sheet layout (per A4)',
    label_margin: 'Page margin (mm)',
    label_gap: 'Spacing (mm)',
    cut_lines: 'Show cut lines',
    show_caption: 'Show caption text',
    generate_preview: 'Generate preview',
    print_a4: 'Print A4 sheet',
    save_pdf: 'Save as PDF',
    generating_pdf: 'Creating PDF…',
    unique_items: 'Rows',
    total_labels: 'Labels total',
    pages_estimate: 'Estimated pages',
    labels_empty: 'Add label values and generate a preview of your A4 sheet.',
    err_labels_empty: 'Please add at least one label value.',
    import_csv: 'Import CSV',
    csv_labels_hint: 'CSV columns: value, caption, quantity. Header row is auto-skipped.',
    csv_imported: 'CSV imported',
    templates: 'Templates',
    save_template: 'Save template',
    load_template: 'Load template',
    template_name: 'Template name',
    template_name_ph: 'e.g. Warehouse shelf A',
    save: 'Save',
    template_saved: 'Template saved',
    template_deleted: 'Template deleted',
    template_loaded: 'Template loaded',
    no_templates: 'No saved templates yet.',
    err_template_name: 'Please enter a template name.',
    import_excel: 'Import Excel/CSV',
    excel_hint: 'CSV or Excel (.xlsx). Columns: value, caption, quantity. Header row is auto-skipped.',
    excel_imported: 'File imported',
    export_template: 'Export',
    import_template: 'Import template',
    template_exported: 'Template exported',
    template_imported: 'Template imported',
    invalid_template_file: 'Invalid template file.',

    footer_rights: 'All rights reserved.',
    imprint: 'Imprint',
    privacy: 'Privacy Policy',
    cookie_policy: 'Cookie Policy',
    terms: 'Terms of Use',
    close: 'Close',

    contact: 'Contact',
    contact_title: 'Contact us',
    contact_desc: 'Have a question or feedback? Send us a message and we will get back to you by email.',
    contact_name: 'Name',
    contact_email: 'Email',
    contact_subject: 'Subject',
    contact_message: 'Message',
    contact_optional: 'optional',
    contact_name_ph: 'Your name',
    contact_email_ph: 'you@example.com',
    contact_subject_ph: 'What is it about?',
    contact_message_ph: 'Your message…',
    contact_send: 'Send message',
    contact_sending: 'Sending…',
    contact_success: 'Thanks! Your message has been sent.',
    contact_error: 'Could not send your message. Please try again.',
    contact_err_name: 'Please enter your name.',
    contact_err_email: 'Please enter a valid email address.',
    contact_err_message: 'Please enter a message.',
    contact_privacy_note: 'Your details are used only to answer your request. See our privacy policy.',

    reviews_title: 'Ratings',
    reviews_subtitle: 'Rate this free tool',
    reviews_of_5: 'out of 5',
    reviews_one: 'rating',
    reviews_many: 'ratings',
    reviews_none: 'No ratings yet — be the first!',
    review_your_rating: 'Your rating',
    review_comment_ph: 'Optional: what do you like or what is missing?',
    review_name_ph: 'Name (optional)',
    review_submit: 'Submit rating',
    review_update: 'Update rating',
    review_sending: 'Saving…',
    review_thanks: 'Thanks for your rating!',
    review_thanks_title: 'Thank you for your review!',
    review_thanks_sub: 'You are helping others discover Barcode Studio.',
    review_share: 'Share Barcode Studio',
    review_share_text: 'Create free barcodes & QR codes with Barcode Studio',
    review_share_copied: 'Link copied to clipboard!',
    review_error: 'Could not save your rating.',
    review_choose_stars: 'Please choose 1 to 5 stars.',
    review_you_rated: 'You rated this',
    reviews_recent: 'Recent reviews',
    rate_button: 'Rate',
    rate_prompt_text: 'Enjoying the tool? Leave a quick rating.',
    rate_prompt_thanks: 'Thanks!',
    review_pending_note: 'Your comment will be published after a short review.',
    stars_word: 'stars',
    admin_messages_link: 'Messages',
    admin_new_suffix: 'new',
    admin_area: 'Admin area',

    cookie_banner_title: 'We use cookies',
    cookie_banner_text: 'We use technically necessary cookies to run this app — e.g. to store your settings, your language and an anonymous device ID that lets you find your history and saved presets. We also keep anonymous usage statistics (e.g. the number and type of generated codes) to operate and improve the service and to show internal metrics in our admin area — no extra cookies are used for this. In addition, we would like to use optional analytics cookies. You decide.',
    accept_all: 'Accept all',
    accept_necessary: 'Only necessary',
    cookie_settings: 'Cookie settings',
    save_selection: 'Save selection',
    cookies_necessary: 'Necessary',
    cookies_necessary_desc: 'Required for the app to work (settings, language, anonymous device ID for history & saved presets, consent). Always active.',
    cookies_analytics: 'Analytics',
    cookies_analytics_desc: 'Optional analytics cookies (PostHog and Google Analytics 4, with IP anonymization) help us understand usage and improve the app. They are only set after you consent here and are removed again if you decline. Independently, we keep cookieless, anonymous usage statistics for internal metrics in our admin area.',
    consent_saved: 'Cookie preferences saved.',
    always_on: 'Always on',
  },
  de: {
    app_title: 'Barcode Studio',
    app_subtitle: 'QR- & 1D-Barcodes erstellen. Export als PNG/SVG. Etiketten + Verlauf.',
    language: 'Sprache',
    tab_generate: 'Erstellen',
    tab_batch: 'Stapel',
    tab_labels: 'Etiketten',
    tab_history: 'Verlauf',

    cat_2d: '2D-Codes',
    cat_1d: '1D-Barcodes',
    gen_subtitle: 'Einen QR-Code oder Barcode erstellen und herunterladen.',
    popular_generators: 'Beliebte Generatoren',
    popular_generators_sub: 'Direkt zu den meistgenutzten Barcode- und QR-Code-Generatoren springen.',
    rate_nudge_title: 'Gefällt dir Barcode Studio?',
    rate_nudge_desc: 'Gib schnell eine Bewertung ab – das dauert nur Sekunden und hilft anderen.',
    rate_nudge_cta: 'Bewerten',
    share_nudge_title: 'Barcode Studio weiterempfehlen',
    share_nudge_desc: 'Gefällt dir das Tool? Teile es mit einem Klick mit Kolleg:innen und Freund:innen.',
    share_nudge_cta: 'Teilen',
    batch_subtitle: 'Viele Codes auf einmal aus einer Liste oder CSV erzeugen.',
    labels_subtitle: 'Etiketten druckfertig auf einem A4-Blatt anordnen.',
    history_subtitle: 'Deine zuletzt erstellten Codes.',
    step: 'Schritt',
    advanced: 'Erweitert',
    advanced_hint: 'Zusätzliche Optionen — nur bei Bedarf.',

    lbl_type: 'Barcode-Typ',
    lbl_value: 'Wert',
    value_placeholder: 'Zu kodierenden Wert eingeben',
    example: 'Beispiel',
    use_example: 'Beispiel verwenden',

    customization: 'Anpassung',
    colors: 'Farben',
    foreground: 'Vordergrund',
    background: 'Hintergrund',
    size: 'Größe',
    bar_width: 'Strichbreite',
    bar_height: 'Strichhöhe',
    qr_scale: 'Modulgröße',
    qr_border: 'Ruhezone',
    text: 'Text',
    show_text: 'Lesbaren Text anzeigen',
    text_size: 'Textgr\u00f6\u00dfe',
    bold_text: 'Fette Schrift',
    scale_text: 'Text mit Barcode skalieren',
    font_family: 'Schriftart der Beschriftung',
    font_sans: 'Serifenlos',
    font_serif: 'Serif',
    font_mono: 'Monospace',
    font_narrow: 'Schmal',
    comparison: 'Vorher / Nachher',
    comparison_before: 'Vorher (Standard)',
    comparison_after: 'Nachher',
    comparison_hint: 'So verändern fette Schrift und Skalierung die Beschriftung.',
    ad_label: 'Werbung',

    qr_content: 'QR-Inhalt',
    content_text: 'Text / URL',
    content_wifi: 'WLAN-Zugang',
    content_bank: 'Banküberweisung (SEPA)',
    wifi_ssid: 'Netzwerkname (SSID)',
    wifi_password: 'Passwort',
    wifi_encryption: 'Verschlüsselung',
    wifi_hidden: 'Verstecktes Netzwerk',
    enc_wpa: 'WPA/WPA2/WPA3',
    enc_wep: 'WEP',
    enc_none: 'Keine (offen)',
    wifi_hint: 'Das Scannen des QR-Codes verbindet das Gerät mit diesem WLAN.',
    bank_name: 'Empfänger',
    bank_iban: 'IBAN',
    bank_bic: 'BIC (optional)',
    bank_amount: 'Betrag € (optional)',
    bank_purpose: 'Verwendungszweck (optional)',
    bank_hint: 'Erstellt einen GiroCode (EPC) für SEPA-Überweisungen — in den meisten Banking-Apps scannbar.',
    content_fields_required: 'Fülle die Pflichtfelder aus, um eine Vorschau zu sehen.',
    iban_invalid: 'IBAN-Prüfsumme scheint nicht zu stimmen — bitte prüfen.',
    print_wifi_card: 'WLAN-Karte drucken',
    wifi_card_title: 'Gäste-WLAN',
    wifi_card_instruction: 'QR-Code mit der Handykamera scannen, um dich zu verbinden.',
    wifi_card_network: 'Netzwerk',
    toggle_theme: 'Dunkelmodus umschalten',
    output: 'Ausgabe',
    output_format: 'Ausgabeformat',
    reset: 'Zurücksetzen',

    presets: 'Vorlagen',
    presets_hint: 'Speichere deine aktuelle Konfiguration und lade sie mit einem Klick.',
    preset_save: 'Vorlage speichern',
    preset_save_title: 'Als Vorlage speichern',
    preset_name: 'Vorlagenname',
    preset_name_placeholder: 'z. B. Produktetikett QR',
    preset_load: 'Vorlage laden',
    preset_select: 'Vorlage wählen…',
    preset_none: 'Noch keine Vorlagen',
    preset_saved: 'Vorlage gespeichert.',
    preset_loaded: 'Vorlage geladen.',
    preset_deleted: 'Vorlage gelöscht.',
    preset_share: 'Teilen-Link kopieren',
    preset_link_copied: 'Teilen-Link in die Zwischenablage kopiert.',
    preset_from_link: 'Vorlage aus geteiltem Link geladen.',
    preset_qr: 'QR-Code anzeigen',
    preset_qr_title: 'Vorlage per QR-Code teilen',
    preset_qr_desc: 'Kollegen können diesen Code scannen, um exakt dieselbe Konfiguration zu öffnen.',
    copy_link: 'Link kopieren',
    preset_delete_confirm: 'Diese Vorlage löschen?',
    preset_delete_desc: 'Diese gespeicherte Konfiguration wird dauerhaft entfernt.',
    preset_name_required: 'Bitte einen Namen eingeben.',
    preset_error: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',

    preview: 'Vorschau',
    preview_empty: 'Wähle einen Barcode-Typ und gib einen Wert ein, um die Vorschau zu sehen.',
    generating: 'Wird erstellt…',
    download: 'Herunterladen',
    download_png: 'PNG herunterladen',
    download_svg: 'SVG herunterladen',
    copy_payload: 'Wert kopieren',
    print: 'Drucken',
    pdf: 'PDF',
    saving_pdf: 'PDF…',
    print_all: 'Alle drucken',
    save_pdf_all: 'Alle als PDF speichern',
    pdf_label_size: 'Etikettengröße (PDF)',
    size_small: 'Klein',
    size_medium: 'Mittel',
    size_large: 'Groß',
    size_custom: 'Eigene',
    columns: 'Spalten',
    rows_label: 'Zeilen',
    per_page: 'pro A4-Seite',
    a4_preview: 'A4-Vorschau',
    pages_word: 'Seiten',
    copied: 'In Zwischenablage kopiert',
    saved_to_history: 'Im Verlauf gespeichert',

    batch_intro: 'Erzeuge viele Codes auf einmal aus einer Liste oder CSV-Datei.',
    input_source: 'Eingabequelle',
    paste_list: 'Liste einfügen',
    upload_csv: 'CSV hochladen',
    paste_placeholder: 'Ein Wert pro Zeile, z. B.\nABC-001\nABC-002\nABC-003',
    csv_hint: 'CSV: die erste Spalte wird als Wert verwendet. Kopfzeile wird automatisch übersprungen.',
    choose_file: 'CSV-Datei auswählen',
    batch_type: 'Barcode-Typ für alle Zeilen',
    batch_generate: 'Stapel erzeugen',
    batch_empty: 'Füge Werte ein oder lade eine CSV hoch, um viele Codes auf einmal zu erzeugen.',
    results: 'Ergebnisse',
    total: 'Gesamt',
    succeeded: 'Erfolgreich',
    failed: 'Fehlgeschlagen',
    download_zip: 'ZIP herunterladen',
    row: 'Zeile',

    history_empty: 'Deine erzeugten Codes erscheinen hier.',
    search: 'Verlauf durchsuchen…',
    clear_all: 'Alle löschen',
    delete: 'Löschen',
    reuse: 'Erneut verwenden',
    no_results: 'Keine passenden Einträge.',
    confirm_clear_title: 'Gesamten Verlauf löschen?',
    confirm_clear_desc: 'Dies entfernt dauerhaft alle gespeicherten Barcodes aus diesem Browser.',
    confirm_delete_title: 'Diesen Eintrag löschen?',
    confirm_delete_desc: 'Dies entfernt den Barcode dauerhaft aus deinem Verlauf.',
    cancel: 'Abbrechen',
    created: 'Erstellt',

    err_empty: 'Bitte einen Wert eingeben.',
    err_generate: 'Barcode konnte nicht erstellt werden.',
    err_batch_empty: 'Bitte mindestens einen Wert angeben.',
    deleted: 'Gelöscht',
    cleared: 'Verlauf geleert',

    labels_intro: 'Viele Barcode-Etiketten druckfertig auf einem A4-Blatt anordnen — ideal für Lager und Industrie.',
    label_items: 'Etiketten',
    label_items_hint: 'Eins pro Zeile: Wert , Beschriftung , Menge. Beschriftung und Menge sind optional.',
    label_placeholder: 'SKU-1001, Artikel A, 6\nSKU-1002, Artikel B, 12\nSKU-1003',
    label_layout: 'Blatt-Layout (pro A4)',
    label_margin: 'Seitenrand (mm)',
    label_gap: 'Abstand (mm)',
    cut_lines: 'Schnittlinien anzeigen',
    show_caption: 'Beschriftungstext anzeigen',
    generate_preview: 'Vorschau erzeugen',
    print_a4: 'A4-Blatt drucken',
    save_pdf: 'Als PDF speichern',
    generating_pdf: 'PDF wird erstellt…',
    unique_items: 'Zeilen',
    total_labels: 'Etiketten gesamt',
    pages_estimate: 'Geschätzte Seiten',
    labels_empty: 'Etikettenwerte hinzufügen und eine Vorschau des A4-Blatts erzeugen.',
    err_labels_empty: 'Bitte mindestens einen Etikettenwert hinzufügen.',
    import_csv: 'CSV importieren',
    csv_labels_hint: 'CSV-Spalten: Wert, Beschriftung, Menge. Kopfzeile wird automatisch übersprungen.',
    csv_imported: 'CSV importiert',
    templates: 'Vorlagen',
    save_template: 'Vorlage speichern',
    load_template: 'Vorlage laden',
    template_name: 'Vorlagenname',
    template_name_ph: 'z. B. Lagerregal A',
    save: 'Speichern',
    template_saved: 'Vorlage gespeichert',
    template_deleted: 'Vorlage gelöscht',
    template_loaded: 'Vorlage geladen',
    no_templates: 'Noch keine gespeicherten Vorlagen.',
    err_template_name: 'Bitte einen Vorlagennamen eingeben.',
    import_excel: 'Excel/CSV importieren',
    excel_hint: 'CSV oder Excel (.xlsx). Spalten: Wert, Beschriftung, Menge. Kopfzeile wird automatisch übersprungen.',
    excel_imported: 'Datei importiert',
    export_template: 'Exportieren',
    import_template: 'Vorlage importieren',
    template_exported: 'Vorlage exportiert',
    template_imported: 'Vorlage importiert',
    invalid_template_file: 'Ungültige Vorlagendatei.',

    footer_rights: 'Alle Rechte vorbehalten.',
    imprint: 'Impressum',
    privacy: 'Datenschutzerklärung',
    cookie_policy: 'Cookie-Richtlinie',
    terms: 'Nutzungsbedingungen (AGB)',
    close: 'Schließen',

    contact: 'Kontakt',
    contact_title: 'Kontakt aufnehmen',
    contact_desc: 'Eine Frage oder Feedback? Schreib uns eine Nachricht – wir melden uns per E-Mail zurück.',
    contact_name: 'Name',
    contact_email: 'E-Mail',
    contact_subject: 'Betreff',
    contact_message: 'Nachricht',
    contact_optional: 'optional',
    contact_name_ph: 'Dein Name',
    contact_email_ph: 'du@beispiel.de',
    contact_subject_ph: 'Worum geht es?',
    contact_message_ph: 'Deine Nachricht…',
    contact_send: 'Nachricht senden',
    contact_sending: 'Wird gesendet…',
    contact_success: 'Danke! Deine Nachricht wurde gesendet.',
    contact_error: 'Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.',
    contact_err_name: 'Bitte gib deinen Namen ein.',
    contact_err_email: 'Bitte gib eine gültige E-Mail-Adresse ein.',
    contact_err_message: 'Bitte gib eine Nachricht ein.',
    contact_privacy_note: 'Deine Angaben werden nur zur Beantwortung deiner Anfrage genutzt. Siehe Datenschutzerklärung.',

    reviews_title: 'Bewertungen',
    reviews_subtitle: 'Bewerte dieses kostenlose Tool',
    reviews_of_5: 'von 5',
    reviews_one: 'Bewertung',
    reviews_many: 'Bewertungen',
    reviews_none: 'Noch keine Bewertungen — sei die/der Erste!',
    review_your_rating: 'Deine Bewertung',
    review_comment_ph: 'Optional: Was gefällt dir oder was fehlt dir?',
    review_name_ph: 'Name (optional)',
    review_submit: 'Bewertung senden',
    review_update: 'Bewertung aktualisieren',
    review_sending: 'Wird gespeichert…',
    review_thanks: 'Danke für deine Bewertung!',
    review_thanks_title: 'Danke für deine Bewertung!',
    review_thanks_sub: 'Du hilfst anderen, Barcode Studio zu entdecken.',
    review_share: 'Barcode Studio teilen',
    review_share_text: 'Erstelle kostenlos Barcodes & QR-Codes mit Barcode Studio',
    review_share_copied: 'Link in die Zwischenablage kopiert!',
    review_error: 'Bewertung konnte nicht gespeichert werden.',
    review_choose_stars: 'Bitte 1 bis 5 Sterne wählen.',
    review_you_rated: 'Deine Bewertung',
    reviews_recent: 'Neueste Bewertungen',
    rate_button: 'Bewerten',
    rate_prompt_text: 'Gefällt dir das Tool? Gib eine kurze Bewertung ab.',
    rate_prompt_thanks: 'Danke!',
    review_pending_note: 'Dein Kommentar wird nach einer kurzen Prüfung veröffentlicht.',
    stars_word: 'Sterne',
    admin_messages_link: 'Nachrichten',
    admin_new_suffix: 'neu',
    admin_area: 'Adminbereich',

    cookie_banner_title: 'Wir verwenden Cookies',
    cookie_banner_text: 'Wir verwenden technisch notwendige Cookies, damit die App funktioniert – z. B. zum Speichern deiner Einstellungen, deiner Sprache und einer anonymen Geräte-ID, mit der du deinen Verlauf und deine gespeicherten Vorlagen wiederfindest. Außerdem führen wir anonyme Nutzungsstatistiken (z. B. Anzahl und Typ erstellter Codes), um den Dienst zu betreiben, zu verbessern und interne Kennzahlen in unserem Adminbereich anzuzeigen – dafür setzen wir keine zusätzlichen Cookies ein. Zusätzlich möchten wir optionale Analyse-Cookies nutzen. Du entscheidest.',
    accept_all: 'Alle akzeptieren',
    accept_necessary: 'Nur notwendige',
    cookie_settings: 'Cookie-Einstellungen',
    save_selection: 'Auswahl speichern',
    cookies_necessary: 'Notwendig',
    cookies_necessary_desc: 'Erforderlich, damit die App funktioniert (Einstellungen, Sprache, anonyme Geräte-ID für Verlauf & gespeicherte Vorlagen, Einwilligung). Immer aktiv.',
    cookies_analytics: 'Analyse',
    cookies_analytics_desc: 'Optionale Analyse-Cookies (PostHog und Google Analytics 4, mit IP-Anonymisierung) helfen uns, die Nutzung zu verstehen und die App zu verbessern. Sie werden nur gesetzt, wenn du hier zustimmst, und bei Ablehnung wieder entfernt. Unabhängig davon führen wir cookielose, anonyme Nutzungsstatistiken für interne Kennzahlen in unserem Adminbereich.',
    consent_saved: 'Cookie-Einstellungen gespeichert.',
    always_on: 'Immer aktiv',
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    migrateLocalStorage({ bc_lang: 'bc_lang' });
    // Allow ?lang=en/de to set the language (used by the static /en/ landing CTA).
    try {
      const u = new URLSearchParams(window.location.search).get('lang');
      if (u === 'en' || u === 'de') { setCookie('bc_lang', u, 365); return u; }
    } catch (e) { console.error('i18n: non-critical error:', e); }
    // Returning visitors keep their saved choice.
    const saved = getCookie('bc_lang');
    if (saved === 'en' || saved === 'de') return saved;
    // First visit: follow the browser's preferred language (English speakers
    // get English automatically), otherwise default to German.
    try {
      const navLangs = (navigator.languages && navigator.languages.length)
        ? navigator.languages
        : [navigator.language || navigator.userLanguage || ''];
      const prefersEn = navLangs.some((l) => String(l).toLowerCase().startsWith('en'));
      const prefersDe = navLangs.some((l) => String(l).toLowerCase().startsWith('de'));
      if (prefersEn && !prefersDe) return 'en';
    } catch (e) { /* noop */ }
    return 'de';
  });
  const changeLang = useCallback((l) => {
    setLang(l);
    setCookie('bc_lang', l, 365);
  }, []);
  const t = useCallback((key) => translations[lang][key] ?? translations.en[key] ?? key, [lang]);
  // Accessibility: keep the document language in sync so screen readers and
  // browsers use the correct pronunciation/hyphenation for the active language.
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch (e) { /* noop */ }
  }, [lang]);
  return (
    <LanguageContext.Provider value={{ lang, setLang: changeLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
}
